import { c as _c } from "react/compiler-runtime";
import { Box, Field, Flex, type FlexProps, HStack, Heading, type HeadingProps, Text, type TextProps } from '@chakra-ui/react';
import React, { type ReactNode } from 'react';

/** The tiny uppercase label that heads every block in Wil's screens. `line` adds the hairline after it. */
export function CrmLabel(props) {
  const $ = _c(26);
  let aside;
  let children;
  let line;
  let mb;
  let mt;
  let my;
  let rest;
  if ($[0] !== props) {
    ({
      line,
      aside,
      children,
      mt,
      mb,
      my,
      ...rest
    } = props);
    $[0] = props;
    $[1] = aside;
    $[2] = children;
    $[3] = line;
    $[4] = mb;
    $[5] = mt;
    $[6] = my;
    $[7] = rest;
  } else {
    aside = $[1];
    children = $[2];
    line = $[3];
    mb = $[4];
    mt = $[5];
    my = $[6];
    rest = $[7];
  }
  if (!line && !aside) {
    let t0;
    if ($[8] !== children || $[9] !== mb || $[10] !== mt || $[11] !== my || $[12] !== rest) {
      t0 = <Text color="fg.subtle" mb={mb} mt={mt} my={my} textStyle="labelXs" {...rest}>{children}</Text>;
      $[8] = children;
      $[9] = mb;
      $[10] = mt;
      $[11] = my;
      $[12] = rest;
      $[13] = t0;
    } else {
      t0 = $[13];
    }
    return t0;
  }
  let t0;
  if ($[14] !== children || $[15] !== rest) {
    t0 = <Text color="fg.subtle" flexShrink={0} textStyle="labelXs" {...rest}>{children}</Text>;
    $[14] = children;
    $[15] = rest;
    $[16] = t0;
  } else {
    t0 = $[16];
  }
  let t1;
  if ($[17] !== line) {
    t1 = line ? <Box bg="border" flex="1" h="1px" /> : <Box flex="1" />;
    $[17] = line;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  let t2;
  if ($[19] !== aside || $[20] !== mb || $[21] !== mt || $[22] !== my || $[23] !== t0 || $[24] !== t1) {
    t2 = <HStack gap={3} mb={mb} mt={mt} my={my} w="full">{t0}{t1}{aside}</HStack>;
    $[19] = aside;
    $[20] = mb;
    $[21] = mt;
    $[22] = my;
    $[23] = t0;
    $[24] = t1;
    $[25] = t2;
  } else {
    t2 = $[25];
  }
  return t2;
}

/** The same small label, wired to the field it sits in, so a click focuses the field and a screen reader names it. */
export function CrmFieldLabel(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Field.Label asChild={true}><CrmLabel as="label" {...props} /></Field.Label>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function CrmTitle(props) {
  const $ = _c(11);
  let aside;
  let children;
  let rest;
  if ($[0] !== props) {
    ({
      aside,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = aside;
    $[2] = children;
    $[3] = rest;
  } else {
    aside = $[1];
    children = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      base: "xl",
      md: "2xl"
    };
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== aside) {
    t1 = aside ? <Text as="span" color="fg.muted" fontSize="0.75em" fontWeight="semibold" ml={2}>{aside}</Text> : null;
    $[5] = aside;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== rest || $[9] !== t1) {
    t2 = <Heading as="h1" fontSize={t0} fontWeight="bold" lineHeight="1.2" {...rest}>{children}{t1}</Heading>;
    $[7] = children;
    $[8] = rest;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
export function CrmHint(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Text color="fg.muted" fontSize="sm" lineHeight="1.5" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** The rows of a page head: title and sub on the left, controls on the right. */
export function CrmPageHead(props) {
  const $ = _c(18);
  let aside;
  let rest;
  let sub;
  let title;
  let titleAside;
  if ($[0] !== props) {
    ({
      aside,
      sub,
      title,
      titleAside,
      ...rest
    } = props);
    $[0] = props;
    $[1] = aside;
    $[2] = rest;
    $[3] = sub;
    $[4] = title;
    $[5] = titleAside;
  } else {
    aside = $[1];
    rest = $[2];
    sub = $[3];
    title = $[4];
    titleAside = $[5];
  }
  let t0;
  if ($[6] !== title || $[7] !== titleAside) {
    t0 = <CrmTitle aside={titleAside}>{title}</CrmTitle>;
    $[6] = title;
    $[7] = titleAside;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  let t1;
  if ($[9] !== sub) {
    t1 = sub ? <CrmHint mt={1}>{sub}</CrmHint> : null;
    $[9] = sub;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== t0 || $[12] !== t1) {
    t2 = <Box minW={0}>{t0}{t1}</Box>;
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== aside || $[15] !== rest || $[16] !== t2) {
    t3 = <Flex align="flex-end" gap={4} justify="space-between" wrap="wrap" {...rest}>{t2}{aside}</Flex>;
    $[14] = aside;
    $[15] = rest;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
}